import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { mountStudioAurora } from "./studio-aurora";
import { mountStudioTickers } from "./studio-ticker";

const CAPABILITIES = ["CONSULT.", "DEVELOP.", "IMPLEMENT.", "CUSTOMISE.", "SUPPORT.", "TEACH."];

const HERO_NOTES = [
	"Built around your business.",
	"Expertly implemented for your business.",
	"Tailored for the way you work.",
	"Implementation without the complexity.",
	"Flexible, scalable, built for growth.",
	"From implementation to ongoing support.",
	"Made practical for growing businesses.",
	"Your workflows, connected."
];

let studioTick = 0;
const tickListeners = new Set();
let studioTimer = null;

function ensureStudioTimer() {
	if (studioTimer !== null) return;
	const motion = matchMedia("(prefers-reduced-motion: reduce)");
	studioTimer = setInterval(() => {
		if (!motion.matches && !document.hidden && document.documentElement.dataset.studioMotion !== "off") {
			studioTick++;
			tickListeners.forEach(listener => listener(studioTick));
		}
	}, 2800);
}

function useStudioSync(totalLength) {
	const [tick, setTick] = useState(studioTick);
	useEffect(() => {
		tickListeners.add(setTick);
		ensureStudioTimer();
		return () => {
			tickListeners.delete(setTick);
			if (tickListeners.size === 0 && studioTimer !== null) {
				clearInterval(studioTimer);
				studioTimer = null;
			}
		};
	}, []);
	return tick % totalLength;
}

function Capability() {
	const index = useStudioSync(CAPABILITIES.length);
	return (
		<span className="studio-capability__line" aria-hidden="true" key={index}>
			<span className="studio-capability__label">We</span>
			<span className="studio-capability__slot">
				<em className="studio-capability__word">{CAPABILITIES[index]}</em>
			</span>
		</span>
	);
}

function HeroNote() {
	const index = useStudioSync(HERO_NOTES.length);
	return (
		<span className="studio-hero-note__line">
			<span className="studio-hero-note__fixed">Open-source ERP.</span>{" "}
			<span className="studio-hero-note__slot" key={index}>
				<span className="studio-hero-note__word">{HERO_NOTES[index]}</span>
			</span>
		</span>
	);
}

function DesignPreview() {
	const [density, setDensity] = useState("relaxed");
	const [motion, setMotion] = useState("system");
	const [open, setOpen] = useState(true);
	useEffect(() => { document.documentElement.dataset.studioDensity = density; }, [density]);
	useEffect(() => { document.documentElement.dataset.studioMotion = motion; }, [motion]);
	if (!open) return null;
	return <aside className="studio-review-panel" aria-label="Design preview controls">
		<strong>Solufy design preview</strong>
		<label>Spacing<select value={density} onChange={event => setDensity(event.target.value)}><option value="relaxed">Relaxed</option><option value="compact">Compact</option></select></label>
		<label>Motion<select value={motion} onChange={event => setMotion(event.target.value)}><option value="system">System</option><option value="off">Off</option></select></label>
		<button type="button" onClick={() => setOpen(false)}>Close preview</button>
	</aside>;
}

export function mountStudio(root = document) {
	mountStudioAurora(root);
	mountStudioTickers(root);
	root.querySelectorAll(".cs-card__media img").forEach(element => {
		const fallback = () => {
			if (element.getAttribute("src")?.startsWith("/assets/solufy_website/images/source/") && !element.dataset.retried) {
				element.dataset.retried = "true";
				element.src = element.getAttribute("src").replace("/images/source/", "/images/");
				return;
			}
			element.hidden = true;
			element.parentElement.classList.add("has-missing-image");
			element.removeEventListener("error", fallback);
		};
		element.addEventListener("error", fallback);
		if (element.complete && !element.naturalWidth) fallback();
	});
	root.querySelectorAll("[data-studio-capability]").forEach(element => {
		if (element.dataset.mounted) return;
		element.dataset.mounted = "true";
		createRoot(element).render(<Capability />);
	});
	root.querySelectorAll("[data-studio-hero-note]").forEach(element => {
		if (element.dataset.mounted) return;
		element.dataset.mounted = "true";
		createRoot(element).render(<HeroNote />);
	});
	if (new URLSearchParams(location.search).get("design-preview") === "1" && !document.querySelector("[data-studio-preview]")) {
		const panel = document.createElement("div");
		panel.dataset.studioPreview = "true";
		document.body.append(panel);
		createRoot(panel).render(<DesignPreview />);
	}
}
